import React, { useCallback, useEffect, useMemo } from 'react';
import cx from 'classnames';
import { withParentSize } from '@visx/responsive';
import { Grid } from '@visx/grid';
import { scaleLinear, scaleTime } from '@visx/scale';
import { LinePath } from '@visx/shape';
import { curveMonotoneX } from '@visx/curve';
import { useTooltip } from '@visx/tooltip';
import { getChartPointFromEvent } from 'js/utils/chart-line-proximity';
import { DateAxisBottom } from 'js/components/Charts/Elements/DateAxisBottom';
import { YearUnderlay } from 'js/components/Charts/Elements/YearUnderlay';
import { useClosestLineHover } from 'js/hooks/useClosestLineHover';
import {
  TREND_Y_DOMAIN,
  buildTrendLineTooltipState,
  findTrendLineTooltipAnchor,
  getTrendLineLastAnchorDatum,
} from 'js/utils/org-trends';
import { useOrgTrends } from './OrgTrendsContext';
import OrgTrendsLineTooltip from './OrgTrendsLineTooltip';

import styles from './OrgTrendsChart.module.scss';

const DEFAULT_MARGINS = { top: 10, right: 15, bottom: 15, left: 15 };

function lineFromMatch(match) {
  if (!match?.id) {
    return null;
  }

  return {
    id: String(match.id),
    label: match.label,
    isRollup: match.series?.isRollup,
    depth: match.series?.depth ?? 0,
  };
}

function OrgTrendsChart({
  parentWidth: width = 0,
  height = 250,
  margins = DEFAULT_MARGINS,
  window,
  trendLines = [],
  lineHoverMaxDistance,
}) {
  margins = { ...DEFAULT_MARGINS, ...margins };
  const chartWidth = Math.max(width - margins.left - margins.right, 0);
  const chartHeight = height - margins.top - margins.bottom;

  const {
    setHoveredLineFromMatch,
    clearHoveredLine,
    toggleLockedLine,
    hoveredLine,
    lockedLine,
  } = useOrgTrends();

  const {
    tooltipData,
    tooltipLeft,
    tooltipTop,
    tooltipOpen,
    showTooltip,
    hideTooltip,
  } = useTooltip();

  const xScale = useMemo(() => {
    const xMaxDate = window?.xMaxDate ?? window?.maxDate;

    if (!window?.minDate || !xMaxDate || chartWidth <= 0) {
      return null;
    }

    return scaleTime({
      domain: [window.minDate, xMaxDate],
      range: [0, chartWidth],
    });
  }, [window, chartWidth]);

  const yScale = useMemo(
    () =>
      scaleLinear({
        domain: TREND_Y_DOMAIN,
        range: [chartHeight, 0],
      }),
    [chartHeight]
  );

  const x = useCallback((d) => xScale?.(d.date) ?? 0, [xScale]);
  const y = useCallback((d) => yScale(d.score), [yScale]);

  const proximitySeries = useMemo(
    () =>
      trendLines.map(({ id, data, label, isRollup, depth }) => ({
        id,
        data,
        label,
        isRollup,
        depth,
      })),
    [trendLines]
  );

  const chartMargin = useMemo(
    () => ({ left: margins.left, top: margins.top }),
    [margins.left, margins.top]
  );

  const showLineTooltip = useCallback(
    (line, anchorDatum) => {
      const tooltipState = buildTrendLineTooltipState({
        line,
        anchorDatum,
        chartWidth,
        chartMargin,
        x,
        y,
      });

      if (tooltipState) {
        showTooltip(tooltipState);
      }
    },
    [chartMargin, chartWidth, showTooltip, x, y]
  );

  const showDefaultLineTooltip = useCallback(
    (line) => {
      const anchorDatum = getTrendLineLastAnchorDatum(line.data);
      showLineTooltip(line, anchorDatum);
    },
    [showLineTooltip]
  );

  const handleActiveLineChange = useCallback(
    (match, event) => {
      if (!match) {
        setHoveredLineFromMatch(null);
        return;
      }

      setHoveredLineFromMatch(lineFromMatch(match));

      const pointer = getChartPointFromEvent({
        event,
        svgElement: event.currentTarget.ownerSVGElement,
        margin: chartMargin,
      });

      if (!pointer) {
        return;
      }

      const anchorDatum = findTrendLineTooltipAnchor({
        data: match.series.data,
        pointerX: pointer.x,
        pointerY: pointer.y,
        projectX: x,
        projectY: y,
      });

      if (!anchorDatum) {
        return;
      }

      showLineTooltip(
        {
          label: match.label,
          isRollup: match.series.isRollup,
          depth: match.series.depth ?? 0,
          data: match.series.data,
        },
        anchorDatum
      );
    },
    [chartMargin, setHoveredLineFromMatch, showLineTooltip, x, y]
  );

  const { handlePointerMove, resolveMatch } = useClosestLineHover({
    series: proximitySeries,
    x,
    y,
    curve: curveMonotoneX,
    maxDistance: lineHoverMaxDistance,
    margin: chartMargin,
    getLabel: (entry) => entry.label,
    onActiveChange: handleActiveLineChange,
  });

  const handleHitAreaClick = useCallback(
    (event) => {
      const match = resolveMatch(event);
      const line = lineFromMatch(match);

      if (line) {
        toggleLockedLine(line);
      }
    },
    [resolveMatch, toggleLockedLine]
  );

  const handleHitAreaLeave = useCallback(() => {
    clearHoveredLine();
  }, [clearHoveredLine]);

  const activeLineId = hoveredLine?.id ?? lockedLine?.id ?? null;

  useEffect(() => {
    if (hoveredLine) {
      return;
    }

    if (!lockedLine) {
      hideTooltip();
      return;
    }

    const line = trendLines.find(
      (entry) => String(entry.id) === String(lockedLine.id)
    );

    if (line) {
      showDefaultLineTooltip(line);
    } else {
      hideTooltip();
    }
  }, [
    hoveredLine,
    lockedLine,
    trendLines,
    hideTooltip,
    showDefaultLineTooltip,
  ]);

  const { backgroundLines, highlightedLine } = useMemo(() => {
    const teamLines = trendLines.filter((line) => !line.isRollup);
    const rollupLines = trendLines
      .filter((line) => line.isRollup)
      .sort((a, b) => b.depth - a.depth);
    const merged = [...teamLines, ...rollupLines];

    if (activeLineId == null) {
      return { backgroundLines: merged, highlightedLine: null };
    }

    const highlighted =
      merged.find((line) => String(line.id) === String(activeLineId)) ?? null;
    const background = merged.filter(
      (line) => String(line.id) !== String(activeLineId)
    );

    return { backgroundLines: background, highlightedLine: highlighted };
  }, [trendLines, activeLineId]);

  const renderTrendLine = useCallback(
    (line, isHighlighted) => {
      if (line.isRollup) {
        return (
          <g
            key={`rollup-${line.id}`}
            className={cx(
              line.paletteClass,
              styles['rollup-line'],
              styles.line,
              {
                'chart-highlight-line': isHighlighted,
                [styles.highlight]: isHighlighted,
                'palette-soft-muted': !isHighlighted,
              }
            )}
          >
            <LinePath
              data={line.data}
              x={x}
              y={y}
              curve={curveMonotoneX}
              className={cx('palette-stroke', {
                'chart-highlight-line': isHighlighted,
              })}
              fill="none"
            />
            {line.data.map((point, pointIndex) => (
              <circle
                key={`${line.id}-${pointIndex}`}
                cx={x(point)}
                cy={y(point)}
                r={isHighlighted ? 3.5 : 2.5}
                stroke="none"
                className="palette-fill"
              />
            ))}
          </g>
        );
      }

      return (
        <g
          key={`team-${line.id}`}
          className={cx(styles.line, {
            [styles.highlight]: isHighlighted,
          })}
        >
          <LinePath
            data={line.data}
            x={x}
            y={y}
            curve={curveMonotoneX}
            className={
              isHighlighted ? 'chart-highlight-line' : 'chart-neutral-line'
            }
            fill="none"
          />
          {line.data.map((point, pointIndex) => (
            <circle
              key={`${line.id}-${pointIndex}`}
              cx={x(point)}
              cy={y(point)}
              r={isHighlighted ? 3 : 2}
              className={styles.dataPoint}
            />
          ))}
        </g>
      );
    },
    [x, y]
  );

  const hasData = xScale && trendLines.some((line) => line.data?.length > 0);

  if (!hasData) {
    return (
      <div className={styles.empty} style={{ height }}>
        No trend data in this window.
      </div>
    );
  }

  return (
    <div className={styles.chart}>
      <svg
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        className="chart-light"
      >
        <g transform={`translate(${margins.left}, ${margins.top})`}>
          <YearUnderlay height={chartHeight} xScale={xScale} />
          <Grid
            className="chart-grid chart-neutral-line"
            height={chartHeight}
            width={chartWidth}
            rowTickValues={[0, 0.5, 1]}
            xScale={xScale}
            yScale={yScale}
            stroke="#526067"
            numTicksColumns={5}
          />
          <DateAxisBottom
            chartHeight={chartHeight}
            chartWidth={chartWidth}
            xScale={xScale}
          />
          <rect
            width={chartWidth}
            height={chartHeight}
            className="chart-box"
            pointerEvents="none"
          />
          <g pointerEvents="none">
            {backgroundLines.map((line) => renderTrendLine(line, false))}
            {highlightedLine && renderTrendLine(highlightedLine, true)}
          </g>
          <rect
            width={chartWidth}
            height={chartHeight}
            className={styles.hitArea}
            pointerEvents="all"
            onMouseMove={handlePointerMove}
            onMouseLeave={handleHitAreaLeave}
            onClick={handleHitAreaClick}
          />
        </g>
      </svg>
      {tooltipOpen && tooltipData && (
        <OrgTrendsLineTooltip
          top={tooltipTop}
          left={tooltipLeft}
          transform={tooltipData.transform}
          label={tooltipData.label}
          isRollup={tooltipData.isRollup}
          depth={tooltipData.depth}
          score={tooltipData.score}
          date={tooltipData.date}
        />
      )}
    </div>
  );
}

export default withParentSize(OrgTrendsChart);
